import { useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowRight, RotateCcw, FlaskConical, Leaf } from "lucide-react";

const STEPS = [
  { key: "hair_type", label: "How would you describe your hair type?", options: ["Straight", "Wavy", "Curly", "Coily"] },
  { key: "scalp_condition", label: "What best describes your scalp?", options: ["Oily", "Dry", "Balanced", "Sensitive"] },
  { key: "primary_concern", label: "What's your main concern right now?", options: ["hair_fall", "dandruff", "dryness", "thinning", "itch"], labels: { hair_fall: "Hair Fall", dandruff: "Dandruff", dryness: "Dryness / Frizz", thinning: "Thinning", itch: "Itch / Irritation" } },
  { key: "severity", label: "How severe is it?", options: ["Mild", "Moderate", "Severe"] },
  { key: "system_preference", label: "Which approach appeals to you?", options: ["Fusion", "Ayurveda", "Allopathy"] },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ system_preference: "Fusion" });
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isNotes = step === STEPS.length;
  const progress = Math.round((step / (STEPS.length + 1)) * 100);

  const choose = (val) => {
    const cur = STEPS[step];
    setAnswers((a) => ({ ...a, [cur.key]: val }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const analyze = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/quiz/analyze", { ...answers, extra_notes: notes });
      setResult(data);
    } catch {
      setResult({ diagnosis: "We couldn't complete the analysis, but here are our best matches.", recommended_products: [] });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({ system_preference: "Fusion" });
    setNotes("");
    setResult(null);
  };

  if (result)
    return (
      <div className="pt-32 md:pt-40 pb-24 bg-cream min-h-screen" data-testid="quiz-result">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <span className="font-sans uppercase tracking-[0.35em] text-[11px] text-amber-deep">
            {result.source === "ai" ? "Your AI Analysis" : "Your Analysis"}
          </span>
          <h1 className="mt-5 font-serif text-4xl md:text-6xl leading-[0.95] text-obsidian max-w-3xl">
            {result.diagnosis}
          </h1>

          {(result.ayurvedic_insight || result.clinical_insight) && (
            <div className="mt-12 grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
              <div className="bg-cream p-8">
                <div className="flex items-center gap-3 text-clinical"><FlaskConical className="w-5 h-5" /><span className="font-sans uppercase tracking-[0.2em] text-[11px]">Clinical view</span></div>
                <p className="mt-4 font-serif text-2xl leading-snug text-obsidian">{result.clinical_insight}</p>
              </div>
              <div className="bg-cream p-8">
                <div className="flex items-center gap-3 text-amber-deep"><Leaf className="w-5 h-5" /><span className="font-sans uppercase tracking-[0.2em] text-[11px]">Ayurvedic view</span></div>
                <p className="mt-4 font-serif text-2xl leading-snug text-obsidian">{result.ayurvedic_insight}</p>
              </div>
            </div>
          )}

          {result.routine?.length > 0 && (
            <div className="mt-14">
              <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 mb-6">Your Ritual</h3>
              <div className="space-y-4">
                {result.routine.map((r, i) => (
                  <div key={i} className="flex gap-5 border-b border-black/10 pb-4">
                    <span className="font-serif text-3xl text-amber/70">0{i + 1}</span>
                    <p className="font-sans font-light text-obsidian/80 self-center">{r}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.recommended_products?.length > 0 && (
            <div className="mt-16">
              <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 mb-8">Recommended for you</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
                {result.recommended_products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              </div>
            </div>
          )}

          <button onClick={reset} data-testid="quiz-restart-btn" className="mt-16 inline-flex items-center gap-2 border border-obsidian/20 px-8 py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-obsidian hover:text-cream transition-colors">
            <RotateCcw className="w-4 h-4" /> Retake analysis
          </button>
        </div>
      </div>
    );

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream min-h-screen" data-testid="quiz-page">
      <div className="mx-auto max-w-[900px] px-5 md:px-10">
        <div className="flex items-center justify-between mb-3">
          <span className="font-sans uppercase tracking-[0.35em] text-[11px] text-amber-deep">AI Scalp Analysis</span>
          <span className="font-sans text-[11px] text-obsidian/50">{Math.min(step + 1, STEPS.length + 1)} / {STEPS.length + 1}</span>
        </div>
        <div className="h-px bg-black/10 mb-16">
          <div className="h-full bg-amber transition-[width] duration-700 ease-out" style={{ width: `${progress}%` }} />
        </div>

        {!isNotes ? (
            <div key={step}>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-obsidian max-w-2xl">{STEPS[step].label}</h2>
              <div className="mt-12 grid sm:grid-cols-2 gap-4">
                {STEPS[step].options.map((o) => (
                  <button
                    key={o}
                    data-testid={`quiz-opt-${o}`}
                    onClick={() => choose(o)}
                    className="text-left border border-black/15 px-7 py-6 font-serif text-2xl text-obsidian hover:bg-obsidian hover:text-cream transition-colors"
                  >
                    {STEPS[step].labels?.[o] || o}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep((s) => s - 1)} className="mt-10 font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/50 hover:text-obsidian">← Back</button>
              )}
            </div>
          ) : (
            <div key="notes">
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-obsidian max-w-2xl">Anything else we should know?</h2>
              <Textarea data-testid="quiz-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. recent stress, diet changes, products you've tried…" className="mt-10 rounded-none border-black/20 min-h-36 text-lg" />
              <button
                onClick={analyze}
                disabled={loading}
                data-testid="quiz-analyze-btn"
                className="mt-8 inline-flex items-center gap-3 bg-obsidian text-cream px-10 py-5 font-sans uppercase tracking-[0.2em] text-xs hover:bg-amber-deep transition-colors disabled:opacity-50"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <>Reveal my analysis <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button onClick={() => setStep((s) => s - 1)} className="mt-6 block font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/50 hover:text-obsidian">← Back</button>
            </div>
          )}
      </div>
    </div>
  );
}
